/**
 * OneBase Proyectos
 * Página de gestión de proyectos del ERP OneBase
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  proyectoService,
  clienteService,
  lineaNegocioService,
  presupuestoVentaService,
  presupuestoCostoService,
} from '../services/onebaseService';
import { exportTableToPDF, formatCurrencyForPDF } from '../utils/pdfExport';
import { exportToExcel, exportToCSV, importFromExcel, importFromCSV, formatDataForExport } from '../utils/importExport';
import AuditHistory from '../components/AuditHistory';
import DetalleProyectoModal from '../components/DetalleProyectoModal';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiEye, FiX, FiUpload } from 'react-icons/fi';
import '../styles/onebase-proyectos.css';

import Loading from '../components/Loading';
const OneBaseProyectos = () => {
  const navigate = useNavigate();
  const [proyectos, setProyectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [lineasNegocio, setLineasNegocio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedProyecto, setSelectedProyecto] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    tipo_proyecto: 'redes_sociales',
    cliente: '',
    linea_negocio: '',
    descripcion: '',
    fecha_cotizacion: '',
    fecha_inicio_planeado: '',
    fecha_termino_planeada: '',
    margen_objetivo: 30,
    estado: 'nuevo',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [totalesProyectos, setTotalesProyectos] = useState({}); // {proyectoId: {totalEjecutado, totalGastado}}

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [proyectosRes, clientesRes, lineasRes] = await Promise.all([
        proyectoService.getAll({ status: 'activo' }),
        clienteService.getAll({ status: 'activo' }),
        lineaNegocioService.getAll({ status: 'activo' }),
      ]);

      // Procesar respuestas - manejar diferentes formatos de API
      const proyectosData = Array.isArray(proyectosRes.data) ? proyectosRes.data : (proyectosRes.data.results || []);
      const clientesData = Array.isArray(clientesRes.data) ? clientesRes.data : (clientesRes.data.results || []);
      const lineasData = Array.isArray(lineasRes.data) ? lineasRes.data : (lineasRes.data.results || []);

      setProyectos(proyectosData);
      setClientes(clientesData);
      setLineasNegocio(lineasData);

      // Cargar totales para cada proyecto
      cargarTotalesProyectos(proyectosData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const cargarTotalesProyectos = async (proyectosData) => {
    try {
      const totales = {};

      // Para cada proyecto, obtener sus presupuestos de costo y calcular totales
      for (const proyecto of proyectosData) {
        try {
          // Obtener presupuestos de costo del proyecto
          const presupuestosResponse = await presupuestoCostoService.getAll({
            proyecto: proyecto.id,
            status: 'activo'
          });
          const presupuestos = Array.isArray(presupuestosResponse.data)
            ? presupuestosResponse.data
            : (presupuestosResponse.data.results || []);

          let totalEjecutado = 0;
          let totalGastado = 0;

          // Para cada presupuesto, obtener sus totales
          for (const presupuesto of presupuestos) {
            try {
              const totalesResponse = await presupuestoCostoService.getTotales(presupuesto.id);
              totalEjecutado += parseFloat(totalesResponse.data.total_ejecutado_costo || 0);
              totalGastado += parseFloat(totalesResponse.data.total_gastado_costo || 0);
            } catch (error) {
              console.error(`Error al cargar totales del presupuesto ${presupuesto.id}:`, error);
            }
          }

          totales[proyecto.id] = {
            totalEjecutado,
            totalGastado
          };
        } catch (error) {
          console.error(`Error al cargar presupuestos del proyecto ${proyecto.id}:`, error);
          totales[proyecto.id] = {
            totalEjecutado: 0,
            totalGastado: 0
          };
        }
      }

      setTotalesProyectos(totales);
    } catch (error) {
      console.error('Error al cargar totales de proyectos:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedProyecto) {
        await proyectoService.update(selectedProyecto.id, formData);
        toast.success('Proyecto actualizado');
      } else {
        await proyectoService.create(formData);
        toast.success('Proyecto creado');
      }
      setShowForm(false);
      resetForm();
      cargarDatos();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar proyecto');
    }
  };

  const handleEdit = (proyecto) => {
    setSelectedProyecto(proyecto);
    setFormData({
      nombre: proyecto.nombre,
      tipo_proyecto: proyecto.tipo_proyecto || 'redes_sociales',
      cliente: proyecto.cliente,
      linea_negocio: proyecto.linea_negocio || '',
      descripcion: proyecto.descripcion || '',
      fecha_cotizacion: proyecto.fecha_cotizacion,
      fecha_inicio_planeado: proyecto.fecha_inicio_planeado || '',
      fecha_termino_planeada: proyecto.fecha_termino_planeada || '',
      margen_objetivo: proyecto.margen_objetivo,
      estado: proyecto.estado,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar este proyecto?')) {
      try {
        await proyectoService.delete(id);
        toast.success('Proyecto eliminado');
        cargarDatos();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await proyectoService.getById(id);
      setSelectedProyecto(response.data);
      setShowDetail(true);
    } catch (error) {
      console.error('Error al cargar detalles:', error);
      toast.error('Error al cargar detalles del proyecto');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      tipo_proyecto: 'redes_sociales',
      cliente: '',
      linea_negocio: '',
      descripcion: '',
      fecha_cotizacion: '',
      fecha_inicio_planeado: '',
      fecha_termino_planeada: '',
      margen_objetivo: 30,
      estado: 'nuevo',
    });
    setSelectedProyecto(null);
  };

  const filteredProyectos = proyectos.filter((p) => {
    // Filtro por búsqueda (nombre o cliente)
    const matchesSearch =
      p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.cliente_nombre.toLowerCase().includes(searchTerm.toLowerCase());

    // Filtro por cliente (p.cliente contiene el ID del cliente)
    const matchesCliente = filterCliente === '' || p.cliente === parseInt(filterCliente);

    // Filtro por estado
    const matchesEstado = filterEstado === '' || p.estado === filterEstado;

    return matchesSearch && matchesCliente && matchesEstado;
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(value || 0);
  };

  const estadoColors = {
    nuevo: '#9ca3af',
    presupuesto_venta_en_trabajo: '#3b82f6',
    presupuesto_venta_en_aprobacion_interna: '#69B32D',
    presupuesto_venta_aprobado_internamente: '#69B32D',
    presupuesto_venta_enviado_a_cliente: '#06b6d4',
    presupuesto_venta_aprobado_cliente: '#10b981',
    en_ejecucion: '#f59e0b',
    terminado: '#6b7280',
    cancelado: '#ef4444',
  };

  const estadoLabels = {
    nuevo: 'Nuevo',
    presupuesto_venta_en_trabajo: 'Presupuesto en Trabajo',
    presupuesto_venta_en_aprobacion_interna: 'Presupuesto en Aprobación Interna',
    presupuesto_venta_aprobado_internamente: 'Presupuesto Aprobado Internamente',
    presupuesto_venta_enviado_a_cliente: 'Presupuesto Enviado a Cliente',
    presupuesto_venta_aprobado_cliente: 'Presupuesto Aprobado por Cliente',
    en_ejecucion: 'En Ejecución',
    terminado: 'Terminado',
    cancelado: 'Cancelado',
  };

  const handleExportProyecto = (proyecto) => {
    try {
      // Calcular valores con workflow incluido
      const costoRealBase = parseFloat(proyecto.costo_real) || 0;
      const costoWorkflow = parseFloat(proyecto.costo_workflow?.costo_total || 0) || 0;
      const costoRealTotal = costoRealBase + costoWorkflow;
      const totalVenta = parseFloat(proyecto.total_venta) || 0;
      const utilidadReal = totalVenta - costoRealTotal;
      const margenReal = totalVenta === 0 ? 0 : ((totalVenta - costoRealTotal) / totalVenta) * 100;

      // Crear objeto con valores actualizados
      const proyectoExport = {
        ...proyecto,
        costo_real: costoRealTotal,
        utilidad: utilidadReal,
        margen_real: margenReal,
      };

      const data = [proyectoExport];
      const columns = [
        { key: 'nombre', label: 'Proyecto' },
        { key: 'cliente_nombre', label: 'Cliente' },
        { key: 'estado', label: 'Estado' },
        { key: 'total_venta', label: 'Presupuestado' },
        { key: 'total_costo', label: 'Costo Esperado' },
      ];
      const filename = `proyecto_${proyecto.nombre}_${new Date().toISOString().split('T')[0]}.pdf`;
      exportTableToPDF('Detalle Proyecto', data, columns, filename);
      toast.success('Proyecto exportado correctamente');
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar proyecto');
    }
  };

  const handleExportAllToExcel = () => {
    try {
      // Calcular valores con workflow incluido para todos los proyectos
      const proyectosConWorkflow = proyectos.map(proyecto => {
        const costoRealBase = parseFloat(proyecto.costo_real) || 0;
        const costoWorkflow = parseFloat(proyecto.costo_workflow?.costo_total || 0) || 0;
        const costoRealTotal = costoRealBase + costoWorkflow;
        const totalVenta = parseFloat(proyecto.total_venta) || 0;
        const utilidadReal = totalVenta - costoRealTotal;
        const margenReal = totalVenta === 0 ? 0 : ((totalVenta - costoRealTotal) / totalVenta) * 100;

        return {
          ...proyecto,
          costo_real: costoRealTotal,
          utilidad: utilidadReal,
          margen_real: margenReal,
        };
      });

      const exportData = formatDataForExport(proyectosConWorkflow, {
        nombre: 'Proyecto',
        cliente_nombre: 'Cliente',
        estado: 'Estado',
        total_venta: 'Presupuestado',
        total_costo: 'Costo Esperado',
        costo_real: 'Costo Real',
        utilidad: 'Utilidad',
        margen_real: 'Margen %',
      });
      const filename = `proyectos_${new Date().toISOString().split('T')[0]}.xlsx`;
      exportToExcel(exportData, filename, 'Proyectos');
      toast.success('Proyectos exportados a Excel');
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar proyectos');
    }
  };

  const handleExportAllToCSV = () => {
    try {
      // Calcular valores con workflow incluido para todos los proyectos
      const proyectosConWorkflow = proyectos.map(proyecto => {
        const costoRealBase = parseFloat(proyecto.costo_real) || 0;
        const costoWorkflow = parseFloat(proyecto.costo_workflow?.costo_total || 0) || 0;
        const costoRealTotal = costoRealBase + costoWorkflow;
        const totalVenta = parseFloat(proyecto.total_venta) || 0;
        const utilidadReal = totalVenta - costoRealTotal;
        const margenReal = totalVenta === 0 ? 0 : ((totalVenta - costoRealTotal) / totalVenta) * 100;

        return {
          ...proyecto,
          costo_real: costoRealTotal,
          utilidad: utilidadReal,
          margen_real: margenReal,
        };
      });

      const exportData = formatDataForExport(proyectosConWorkflow, {
        nombre: 'Proyecto',
        cliente_nombre: 'Cliente',
        estado: 'Estado',
        total_venta: 'Presupuestado',
        total_costo: 'Costo Esperado',
        costo_real: 'Costo Real',
        utilidad: 'Utilidad',
        margen_real: 'Margen %',
      });
      const filename = `proyectos_${new Date().toISOString().split('T')[0]}.csv`;
      exportToCSV(exportData, filename);
      toast.success('Proyectos exportados a CSV');
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar proyectos');
    }
  };

  const handleImportFile = (file) => {
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const handler = isExcel ? importFromExcel : importFromCSV;

    handler(
      file,
      (data) => {
        // Handle imported data
        console.log('Imported data:', data);
        toast.info(`Se cargaron ${data.length} registros. Por favor revise e importarlos manualmente.`);
      },
      (error) => {
        console.error('Import error:', error);
        toast.error('Error al importar archivo');
      }
    );
  };

  const openImportDialog = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleImportFile(file);
      }
    };
    input.click();
  };

  return (
    <div className="onebase-proyectos">
      <div className="page-header">
        <h1>Proyectos</h1>
        <div className="header-actions">
          <button
            className="btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            <FiPlus /> Nuevo Proyecto
          </button>
        </div>
      </div>

      {/* Barra de búsqueda y filtros */}
      <div className="search-and-filters">
        <input
          type="text"
          className="search-input"
          placeholder="Buscar por nombre o cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="filter-select"
          value={filterCliente}
          onChange={(e) => setFilterCliente(e.target.value)}
        >
          <option value="">Todos los clientes</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterEstado}
          onChange={(e) => setFilterEstado(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="nuevo">Nuevo</option>
          <option value="presupuesto_venta_en_trabajo">Presupuesto en Trabajo</option>
          <option value="presupuesto_venta_en_aprobacion_interna">Presupuesto en Aprobación Interna</option>
          <option value="presupuesto_venta_aprobado_internamente">Presupuesto Aprobado Internamente</option>
          <option value="presupuesto_venta_enviado_a_cliente">Presupuesto Enviado a Cliente</option>
          <option value="presupuesto_venta_aprobado_cliente">Presupuesto Aprobado por Cliente</option>
          <option value="en_ejecucion">En Ejecución</option>
          <option value="terminado">Terminado</option>
          <option value="cancelado">Cancelado</option>
        </select>
      </div>

      {/* Tabla de proyectos */}
      {loading ? (
        <Loading fullScreen={false} />
      ) : filteredProyectos.length > 0 ? (
        <div className="proyectos-table">
          <table>
            <thead>
              <tr>
                <th>Proyecto</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>Presupuestado</th>
                <th>Costo Esperado</th>
                <th>Total Ejecutado</th>
                <th>Total Gastado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredProyectos.map((proyecto) => (
                <tr key={proyecto.id}>
                  <td className="proyecto-name">
                    <strong>{proyecto.nombre}</strong>
                  </td>
                  <td>{proyecto.cliente_nombre}</td>
                  <td>
                    <span
                      className="estado-badge"
                      style={{ backgroundColor: estadoColors[proyecto.estado] }}
                    >
                      {estadoLabels[proyecto.estado] || proyecto.estado}
                    </span>
                  </td>
                  <td>{formatCurrency(proyecto.total_venta)}</td>
                  <td>{formatCurrency(proyecto.total_costo)}</td>
                  <td>{formatCurrency(totalesProyectos[proyecto.id]?.totalEjecutado || 0)}</td>
                  <td>{formatCurrency(totalesProyectos[proyecto.id]?.totalGastado || 0)}</td>
                  <td className="acciones">
                    <button
                      className="btn-icon"
                      onClick={() => handleViewDetails(proyecto.id)}
                      title="Ver detalles"
                    >
                      <FiEye />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleDelete(proyecto.id)}
                      title="Eliminar"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <p>No hay proyectos</p>
          <button
            className="btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            Crear primer proyecto
          </button>
        </div>
      )}

      {/* Modal de formulario */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedProyecto ? 'Editar Proyecto' : 'Nuevo Proyecto'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Tipo de Proyecto *</label>
                <select
                  name="tipo_proyecto"
                  value={formData.tipo_proyecto}
                  onChange={handleInputChange}
                  required
                >
                  <option value="redes_sociales">Redes Sociales</option>
                  <option value="evento">Evento</option>
                  <option value="proyecto">Proyecto</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cliente *</label>
                  <select
                    name="cliente"
                    value={formData.cliente}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Línea de Negocio</label>
                  <select
                    name="linea_negocio"
                    value={formData.linea_negocio}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar...</option>
                    {lineasNegocio.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha de Cotización *</label>
                  <input
                    type="date"
                    name="fecha_cotizacion"
                    value={formData.fecha_cotizacion}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Margen Objetivo (%)</label>
                  <input
                    type="number"
                    name="margen_objetivo"
                    value={formData.margen_objetivo}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.5"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha Inicio Planeado</label>
                  <input
                    type="date"
                    name="fecha_inicio_planeado"
                    value={formData.fecha_inicio_planeado}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Fecha Término Planeada</label>
                  <input
                    type="date"
                    name="fecha_termino_planeada"
                    value={formData.fecha_termino_planeada}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Estado</label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                >
                  <option value="nuevo">Nuevo</option>
                  <option value="cotizado">Cotizado</option>
                  <option value="aprobado">Aprobado</option>
                  <option value="en_ejecucion">En Ejecución</option>
                  <option value="terminado">Terminado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {selectedProyecto ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de detalles con pestañas */}
      {showDetail && selectedProyecto && (
        <DetalleProyectoModal
          proyecto={selectedProyecto}
          onClose={() => setShowDetail(false)}
          onUpdate={async () => {
            // Recargar datos del proyecto sin cerrar el modal
            await cargarDatos();
            // Buscar el proyecto actualizado en la lista
            const proyectoActualizado = proyectos.find(p => p.id === selectedProyecto.id);
            if (proyectoActualizado) {
              setSelectedProyecto(proyectoActualizado);
            }
          }}
        />
      )}
    </div>
  );
};

export default OneBaseProyectos;
